import {Chip, Stack, Typography} from '@mui/material';
import type {NavigationTreeSite, NavigationTreePlan, NavigationTreeCage} from 'client';
import {type TreeSelection} from './AssignTree';

interface TagListProps {
  data: NavigationTreePlan;
  setSelected: (selection: TreeSelection) => void;
  selected?: TreeSelection;
  open: boolean;
  disabled?: boolean;
}

const LIMIT_TAGS_FOCUS: number = 2;
const LIMIT_TAGS_BLUR: number = 20;
const SITE_NAME_LIMIT: number = 7;

export const TagList = ({data, setSelected, selected, open, disabled}: TagListProps): JSX.Element => {
  const limitTags = open ? LIMIT_TAGS_BLUR : LIMIT_TAGS_FOCUS;
  return (
    <Stack direction={'row'} gap={0.6} p={2} pl={1} flexWrap={'wrap'} width={'100%'}>
      {selected &&
        selected.cageIds.slice(0, limitTags).map((value) => (
          <Chip
            disabled={disabled}
            size="small"
            color="secondary"
            key={value}
            sx={{py: 1}}
            onMouseDown={(event) => {
              if (disabled) {
                return;
              }
              event.stopPropagation();
            }}
            onDelete={() => {
              if (!disabled && selected) {
                const newList = removeCage(selected, value);
                setSelected(newList);
              }
            }}
            label={<Typography variant="body3">{findItemByIdAndGetName(data, value)}</Typography>}
          />
        ))}
      <Typography variant="body1" pt={0.2} pl={0.6}>
        {selected && inputText(open, selected.cageIds.length)}
      </Typography>
    </Stack>
  );
};

const findItemByIdAndGetName = (data: NavigationTreePlan, cageId: string): string => {
  if (!data.sites) {
    return '';
  }

  const foundCage = data.sites
    .flatMap(
      (site: NavigationTreeSite) =>
        site.cages?.map((cage: NavigationTreeCage) => ({
          siteName: site.name,
          cageName: cage.name,
          id: cage.id,
        })) ?? [],
    )
    .find((item) => item.id === cageId);

  return foundCage ? `${truncateString(foundCage.siteName)}/${foundCage.cageName}` : '';
};

const removeCage = (selected: TreeSelection, cageId: string): TreeSelection => {
  const cageIds: string[] = selected.cageIds && selected.cageIds.filter((cage: string) => cage !== cageId);
  return {siteIds: selected.siteIds, cageIds};
};

const truncateString = (name: string): string => {
  const maxLength = SITE_NAME_LIMIT;
  if (name.length > maxLength) {
    return name.substring(0, maxLength) + '...';
  } else {
    return name;
  }
};

const inputText = (open: boolean, length: number): string => {
  if (length === 0) {
    return 'Select cages';
  } else if (!open && length > LIMIT_TAGS_FOCUS) {
    return `+${length - LIMIT_TAGS_FOCUS}`;
  } else {
    return '';
  }
};
